import React, { useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePageChat } from '@/hooks';
import { ChatInput } from '../components/chat/ChatInput';
import { QuickSuggestionsBlock } from '../components/chat/QuickSuggestionsBlock';
import { Header } from '@components/layout';
import { Footer } from '@components/layout';
import { ChatContainer } from '../components/chat/ChatContainer';
import { LoadingState } from '../components/common/LoadingState';
import { SchemaMarkup } from '@/components/common/SchemaMarkup';
import { getDealershipBySlug } from '@/config/dealerships.config';
import { getVehicles } from '@/config/vehicles.config';

interface DealershipPageProps {
    showBanner: () => void;
}

export const DealershipPage: React.FC<DealershipPageProps> = ({ showBanner }) => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { messages, isLoading, streamingMessage, sendMessage, assistant, loadingAssistants, messagesEndRef, currentTool } = usePageChat(slug);
    const dealership = slug ? getDealershipBySlug(slug) : undefined;
    const vehiclePageMap = useMemo(() => Object.fromEntries(getVehicles().map(v => [`${v.make} ${v.model}`.toLowerCase(), v.path])), []);

    // Loading state
    if (loadingAssistants || !assistant) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <LoadingState message="Loading..." />
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Dealership not found
    if (!dealership) {
        return (
            <>
                <Header />
                <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                    <div className="max-w-3xl mx-auto">
                        <div className="text-center text-white">
                            <h1 className="text-2xl font-bold mb-4">Dealership Not Found</h1>
                            <p className="text-gray-400 mb-6">The dealership you're looking for doesn't exist.</p>
                            <button
                                onClick={() => navigate('/dealerships')}
                                className="px-6 py-2 bg-cyan-400 hover:bg-cyan-500 text-black font-semibold rounded-full transition-all"
                            >
                                Browse Dealerships
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const dealershipBusinessSchema = dealership ? {
        '@context': 'https://schema.org',
        '@type': 'AutomotiveBusiness',
        name: dealership.name,
        address: {
            '@type': 'PostalAddress',
            streetAddress: dealership.address.street,
            addressLocality: dealership.address.city,
            addressRegion: dealership.address.province,
            postalCode: dealership.address.postalCode ?? '',
            addressCountry: 'CA',
        },
        telephone: dealership.phone,
        areaServed: dealership.serviceArea.join(', '),
        description: dealership.intro,
    } : null;

    const dealershipFaqSchema = dealership && dealership.faqs.length > 0 ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: dealership.faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    } : null;

    const dealershipSchemas = dealership
        ? [dealershipBusinessSchema!, ...(dealershipFaqSchema ? [dealershipFaqSchema] : [])]
        : null;

    return (
        <>
            {dealership && dealershipSchemas && <SchemaMarkup schema={dealershipSchemas} />}
            <Header />
            <div className="pt-20 sm:pt-24 md:pt-28 pb-28 sm:pb-32 md:pb-36 px-2 sm:px-3 md:px-4">
                <div className="max-w-3xl mx-auto">
                    <div className="min-h-[60vh] space-y-2 sm:space-y-3 md:space-y-4 py-2 sm:py-3 md:py-4">
                        <article className="mb-6">
                            {/* 1. H1 */}
                            <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-white mb-4 sm:mb-6">
                                {dealership.h1}
                            </h1>

                            {/* 2. Intro paragraph */}
                            <p className="text-sm sm:text-base text-gray-300 mb-4 sm:mb-6">
                                {dealership.intro}
                            </p>

                            {/* 3. Quick suggestion buttons */}
                            <QuickSuggestionsBlock
                                topLine="Not sure where to start?"
                                title={`Ask anything about vehicles or services at ${dealership.name}.`}
                                suggestions={dealership.quickSuggestions}
                                onSend={sendMessage}
                            />

                            {/* 4. About section */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    About {dealership.name}
                                </h2>
                                <p className="text-sm sm:text-base text-gray-300">
                                    {dealership.about}
                                </p>
                            </div>

                            {/* 5. Location and Contact Information */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Location and Contact Information
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    <li>
                                        <a
                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${dealership.name}, ${dealership.address.street}, ${dealership.address.city}, ${dealership.address.province}`)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-cyan-400 hover:text-cyan-300 underline transition-colors"
                                        >
                                            {dealership.address.street}, {dealership.address.city}, {dealership.address.province}{dealership.address.postalCode ? ` ${dealership.address.postalCode}` : ''}
                                        </a>
                                    </li>
                                    <li>Phone: {dealership.phone}</li>
                                </ul>
                            </div>

                            {/* 6. Brand Vehicles Available */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    {dealership.brand} Vehicles Available at {dealership.name}
                                </h2>
                                {dealership.vehicleCategories.map((cat, i) => (
                                    <div key={i} className="mb-3">
                                        <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                            {cat.category}
                                        </h3>
                                        <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                            {cat.models.map((model, j) => {
                                                const path = vehiclePageMap[model.toLowerCase()];
                                                return (
                                                    <li key={j}>
                                                        {path
                                                            ? <Link to={path} className="text-cyan-400 hover:text-cyan-300 hover:underline transition-colors">{model}</Link>
                                                            : model
                                                        }
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>

                            {/* 7. Vehicles Available Near [City] */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Vehicles Available Near {dealership.city}
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {dealership.nearbyVehicles.map((vehicle, i) => (
                                        <li key={i}>{vehicle}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* 8. Brand content (if present) */}
                            {dealership.brandContent && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        {dealership.brandContent.heading}
                                    </h2>
                                    <p className="text-sm sm:text-base text-gray-300">
                                        {dealership.brandContent.body}
                                    </p>
                                </div>
                            )}

                            {/* 9. Vehicle Service and Maintenance */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Vehicle Service and Maintenance
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {dealership.services.map((service, i) => (
                                        <li key={i}>{service}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* 10. Serving Drivers Across Southern Saskatchewan */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Serving Drivers Across Southern Saskatchewan
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {dealership.serviceArea.map((area, i) => (
                                        <li key={i}>{area}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* 11. Research Vehicles Before Visiting a Dealership */}
                            <div className="mb-4">
                                <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                    Research Vehicles Before Visiting a Dealership
                                </h2>
                                <ul className="list-disc list-inside space-y-1.5 sm:space-y-2 text-sm sm:text-base text-gray-300 ml-2">
                                    {dealership.researchTopics.map((topic, i) => (
                                        <li key={i}>{topic}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* 12. Common Questions */}
                            {dealership.faqs.length > 0 && (
                                <div className="mb-4">
                                    <h2 className="text-base sm:text-xl font-bold text-white mt-4 sm:mt-6 mb-2 sm:mb-3">
                                        Common Questions
                                    </h2>
                                    {dealership.faqs.map((faq, i) => (
                                        <div key={i} className="mb-3">
                                            <h3 className="text-sm sm:text-lg font-semibold text-gray-200 mt-3 sm:mt-4 mb-1.5 sm:mb-2">
                                                {faq.question}
                                            </h3>
                                            <p className="text-sm sm:text-base text-gray-300">
                                                {faq.answer}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </article>

                        {/* Follow-up Chat Messages */}
                        {messages.length > 0 && (
                            <ChatContainer
                                messages={messages}
                                assistant={assistant}
                                isLoading={isLoading}
                                streamingMessage={streamingMessage}
                                currentTool={currentTool}
                            />
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </div>
            </div>
            <ChatInput
                assistant={assistant}
                onSendMessage={sendMessage}
                disabled={isLoading}
                showBanner={showBanner}
            />
        </>
    );
};
